export interface BeforeInstallPromptChoice {
  readonly outcome: "accepted" | "dismissed";
  readonly platform: string;
}

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<BeforeInstallPromptChoice>;
}

interface ServiceWorkerContainerLike {
  register: (scriptUrl: string) => Promise<unknown>;
}

interface NavigatorLike {
  readonly serviceWorker?: ServiceWorkerContainerLike;
}

interface WindowLike {
  readonly location: {
    readonly hostname: string;
  };
  readonly isSecureContext?: boolean;
  readonly reportError?: (error: unknown) => void;
  addEventListener: (
    type: string,
    listener: EventListenerOrEventListenerObject,
    options?: AddEventListenerOptions,
  ) => void;
}

interface DocumentLike {
  readonly readyState: string;
}

interface RegisterServiceWorkerOptions {
  readonly documentLike?: DocumentLike;
  readonly navigatorLike?: NavigatorLike;
  readonly windowLike?: WindowLike;
}

function isLocalhost(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

export function canRegisterServiceWorker(
  windowLike: WindowLike,
  navigatorLike: NavigatorLike,
): boolean {
  if (!navigatorLike.serviceWorker) {
    return false;
  }

  return windowLike.isSecureContext === true || isLocalhost(windowLike.location.hostname);
}

export function registerAppServiceWorker({
  documentLike = document,
  navigatorLike = window.navigator,
  windowLike = window,
}: RegisterServiceWorkerOptions = {}): void {
  if (!canRegisterServiceWorker(windowLike, navigatorLike)) {
    return;
  }

  const register = () => {
    void navigatorLike.serviceWorker?.register("/sw.js").catch((error) => {
      windowLike.reportError?.(error);
    });
  };

  if (documentLike.readyState === "complete") {
    register();
    return;
  }

  windowLike.addEventListener("load", register, { once: true });
}
